import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../context/UserProvider';

export default function AccessibilityDrawerSettings() {
  const {
    darkMode,
    setDarkMode,
    isExpanded,
    setUserPreference,
    hideInActive,
    setHideInActive,
    hideDenied,
    setHideDenied,
    colorblindMode,
    setColorblindMode,
    localMode,
    setLocalMode,
    localFont,
    setLocalFont,
    localFontSize,
    setLocalFontSize,
    accessibilityHidden,
    setAccessibilityHidden,
  } = useContext(UserContext);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const originalColorblind = useRef(colorblindMode);
  const originalFont = useRef(localStorage.getItem("selectedFont") || "none");
  const originalFontSize = useRef(localStorage.getItem("selectedFontSize") || "16px");
  const modalRef = useRef(null);
  const lastClickTime = useRef(0);

  const fontSizeScaleMap = {
    "12px": 0.75,
    "14px": 0.875,
    "16px": 1.0,
    "20px": 1.25,
    "24px": 1.5,
  };

  useEffect(() => {
    setLocalMode(colorblindMode);
  }, []);

  useEffect(() => {
    const fonts = {
      none: "",
      dyslexia: '"OpenDyslexic", sans-serif',
      serif: "Georgia, serif",
      sansSerif: "Arial, sans-serif",
      monospace: '"Courier New", monospace',
    };
    document.body.style.fontFamily = fonts[localFont] || "";
    const scale = fontSizeScaleMap[localFontSize] || 1.0;
    document.documentElement.style.setProperty("--root-font-size", `${16 * scale}px`);
  }, [localFont, localFontSize]);

  useEffect(() => {
    if (localMode !== colorblindMode) {
      setColorblindMode(localMode);
    }
  }, [localMode, setColorblindMode, colorblindMode]);

  const handleDarkMode = async (e) => {
    const newDarkModePreference = e.target.value === "on";
    setDarkMode(newDarkModePreference);
    if (newDarkModePreference) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    try {
      await setUserPreference({
        newDarkMode: newDarkModePreference,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating dark mode:", error);
      setDarkMode(!newDarkModePreference);
      if (newDarkModePreference) {
        document.body.classList.remove('dark');
      } else {
        document.body.classList.add('dark');
      }
    }
  };

  const handleInActive = async (e) => {
    const newHideInActive = e.target.value === "on";
    setHideInActive(newHideInActive);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating hide inactive:", error);
      setHideInActive(!newHideInActive);
    }
  };

  const handleDenied = async (e) => {
    const newHideDenied = e.target.value === "on";
    setHideDenied(newHideDenied);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating hide denied:", error);
      setHideDenied(!newHideDenied);
    }
  };

  const handleColorblindChange = async (e) => {
    const newLocalMode = e.target.value;
    setLocalMode(newLocalMode);
    setColorblindMode(newLocalMode);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: newLocalMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating colorblind mode:", error);
      setLocalMode(originalColorblind.current);
      setColorblindMode(originalColorblind.current);
    }
  };

  const handleFontChange = async (e) => {
    const newLocalFont = e.target.value;
    setLocalFont(newLocalFont);
    localStorage.setItem("selectedFont", newLocalFont);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: newLocalFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating font:", error);
      setLocalFont(originalFont.current);
      localStorage.setItem("selectedFont", originalFont.current);
    }
  };

  const handleFontSizeChange = async (e) => {
    const newLocalFontSize = e.target.value;
    setLocalFontSize(newLocalFontSize);
    localStorage.setItem("selectedFontSize", newLocalFontSize);
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: newLocalFontSize,
        newAccessibilityHidden: accessibilityHidden,
      });
    } catch (error) {
      console.error("Error updating font size:", error);
      setLocalFontSize(originalFontSize.current);
      localStorage.setItem("selectedFontSize", originalFontSize.current);
    }
  };

  const handleHideAccessibilityChange = (e) => {
    const newValue = e.target.value;
    if (newValue === "on") {
      const now = Date.now();
      if (now - lastClickTime.current > 300) {
        lastClickTime.current = now;
        setShowWarningModal(true);
      }
    } else {
      handleConfirmHide(false);
    }
  };

  const handleConfirmHide = async (shouldHide) => {
    const newHiddenState = shouldHide;
    setAccessibilityHidden(newHiddenState);
    localStorage.setItem("accessibilityHidden", newHiddenState.toString());
    try {
      await setUserPreference({
        newDarkMode: darkMode,
        newIsExpanded: isExpanded,
        newHideInActive: hideInActive,
        newHideDenied: hideDenied,
        newColorblindMode: localMode,
        newLocalFont: localFont,
        newLocalFontSize: localFontSize,
        newAccessibilityHidden: newHiddenState,
      });
      setShowWarningModal(false);
    } catch (error) {
      console.error("Error updating accessibility hidden:", error);
      setAccessibilityHidden(!newHiddenState);
      localStorage.setItem("accessibilityHidden", (!newHiddenState).toString());
    }
  };

  const handleCancelHide = () => {
    setShowWarningModal(false);
  };

  return (
    <div id="preferences" className="w-full px-4 py-6 sm:w-full sm:px-12 overflow-y-auto max-h-screen">
      <div className="max-w-4xl rounded-lg bg-white p-6 dark:bg-stone-950">
        <div className="space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Dark Mode
            </label>
            <select
              value={darkMode ? "on" : "off"}
              onChange={handleDarkMode}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Inactive
            </label>
            <select
              value={hideInActive ? "on" : "off"}
              onChange={handleInActive}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Denied
            </label>
            <select
              value={hideDenied ? "on" : "off"}
              onChange={handleDenied}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Colorblind Mode
            </label>
            <select
              value={localMode}
              onChange={handleColorblindChange}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="none">Default</option>
              <option value="protanopia">Protanopia</option>
              <option value="protanomaly">Protanomaly</option>
              <option value="deuteranopia">Deuteranopia</option>
              <option value="tritanopia">Tritanopia</option>
              <option value="tritanomaly">Tritanomaly</option>
              <option value="achromatopsia">Achromatopsia</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Font
            </label>
            <select
              value={localFont}
              onChange={handleFontChange}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="none">Default</option>
              <option value="dyslexia">Dyslexic</option>
              <option value="serif">Serif</option>
              <option value="sansSerif">Sans-serif</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Font Size
            </label>
            <select
              value={localFontSize}
              onChange={handleFontSizeChange}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="12px">Extra Small (12px)</option>
              <option value="14px">Small (14px)</option>
              <option value="16px">Default (16px)</option>
              <option value="20px">Large (20px)</option>
              <option value="24px">Extra Large (24px)</option>
            </select>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-800 dark:text-gray-300">
              Hide Accessibility
            </label>
            <select
              value={accessibilityHidden ? "on" : "off"}
              onChange={handleHideAccessibilityChange}
              className="w-full rounded-md border-2 border-gray-300 bg-white p-2 text-gray-800 shadow-sm dark:border-gray-700 dark:bg-stone-800 dark:text-gray-300"
            >
              <option value="off">Off</option>
              <option value="on">On</option>
            </select>
          </div>
        </div>
      </div>

      {showWarningModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-8 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-stone-950 bg-opacity-75"></div>
            </div>
            <div className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" />
            <div
              ref={modalRef}
              className="inline-block align-bottom bg-white dark:bg-stone-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
            >
              <div className="flex flex-col h-full">
                <div className="bg-white dark:bg-stone-800 px-4 py-6 sm:p-6 flex-grow flex items-center justify-center">
                  <div className="sm:flex sm:items-center">
                    <div className="text-center sm:text-left">
                      <h3 className="pt-6 text-lg leading-6 font-semibold text-gray-900 dark:text-gray-200">
                        Hide Accessibility Button
                      </h3>
                      <div className="mt-1 dark:text-gray-300">
                        Are you sure you want to hide the accessibility button? You can re-enable it in these settings later.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-stone-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse justify-center">
                  <button
                    onClick={() => handleConfirmHide(true)}
                    className="mt-3 w-28 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white bg-blue-500 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none sm:ml-2 sm:text-sm sm:mt-0"
                  >
                    Yes
                  </button>
                  <button
                    onClick={handleCancelHide}
                    className="mt-3 w-28 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none sm:ml-2 sm:text-sm sm:mt-0"
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}